import * as Tabs from "@radix-ui/react-tabs";
import clsx from "clsx";
import * as React from "react";
import {
  sheetTabsBorderRadiusDefault,
  sheetTabsBorderRadiusFirstSelected,
  sheetTabsContainer,
  sheetTabsContent,
  sheetTabsHeaderInner,
  sheetTabsHeaderWrap,
  sheetTabsList,
  sheetTabsTrigger
} from "./SheetTabs.css";

/** Public types */
export type TabItem = {
  /**
   * The value of the tab, which corresponds to the `value` prop of the `Tabs.Content` component.
   */
  value: string;
  /**
   * The label of the tab, which is displayed in the tab list.
   */
  label: string;
};

/** Root */
function Root({
  children,
  ...props
}: React.PropsWithChildren & Tabs.TabsProps) {
  return (
    <Tabs.Root
      {...props}
      className={sheetTabsContainer}
      activationMode="manual"
    >
      {children}
    </Tabs.Root>
  );
}

/** Header (layout only) */
function Header({ children }: React.PropsWithChildren) {
  return (
    <div className={sheetTabsHeaderWrap}>
      <div className={sheetTabsHeaderInner}>{children}</div>
    </div>
  );
}

/** List (Radix List) */
function List({
  children,
  ...props
}: React.PropsWithChildren & Tabs.TabsListProps) {
  return (
    <Tabs.List className={sheetTabsList} {...props}>
      {children}
    </Tabs.List>
  );
}

/** Trigger (Radix Trigger) */
function Trigger({
  children,
  ...props
}: React.PropsWithChildren & Tabs.TabsTriggerProps) {
  return (
    <Tabs.Trigger className={sheetTabsTrigger} {...props}>
      {children}
    </Tabs.Trigger>
  );
}

/** Content Wrapper (layout only styling) */
function ContentWrapper({
  children,
  activeValue,
  firstValue,
  className,
  style
}: {
  activeValue?: string;
  firstValue?: string;
  className?: string;
  /** Inline layout overrides — e.g. a drag-resized panel height. */
  style?: React.CSSProperties;
} & React.PropsWithChildren) {
  return (
    <div
      className={clsx(
        activeValue === firstValue
          ? sheetTabsBorderRadiusFirstSelected
          : sheetTabsBorderRadiusDefault,
        sheetTabsContent,
        className
      )}
      style={style}
    >
      {children}
    </div>
  );
}

/** Content (Radix Content) */
function Content({ children, ...rest }: Tabs.TabsContentProps) {
  return <Tabs.Content {...rest}>{children}</Tabs.Content>;
}

/** Namespace export */
export const SheetTabs = Object.assign(Root, {
  Header,
  List,
  Trigger,
  ContentWrapper,
  Content
});
